import * as React from 'react';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { formatWithTimeZoneName } from 'owa-timezone';
import { getCalendarConfigWorkingHoursTimeZoneId } from 'owa-calendar-configuration';
import { getUserTimeZone } from 'owa-session-store';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { observer } from 'mobx-react';
import { OwaDate, utcDate } from 'owa-datetime/lib/owaDate/owaDate';
import addMinutes from 'owa-datetime/lib/owaDate/addMinutes';
import differenceInMinutes from 'owa-datetime/lib/owaDate/differenceInMinutes';
import getTimeIncrement from '../selectors/getTimeIncrement';
import getWorkingHoursEndTimeInMinutes from '../selectors/getWorkingHoursEndTime';
import getWorkingHoursStartTimeInMinutes from '../selectors/getWorkingHoursStartTime';
import isValidTimeSelection from '../utils/isValidTimeSelection';
import setWorkingHoursTimeZoneId from '../data/mutators/setWorkingHoursTimeZoneId';
import setWorkingHoursEndTimeOption from '../data/mutators/setWorkingHoursEndTimeOption';
import setWorkingHoursStartTimeOption from '../data/mutators/setWorkingHoursStartTimeOption';
import strings from 'owa-strings/owa-options-strings';
import TimePicker from 'owa-timepicker/lib/components/TimePicker';
import store from '../data/store/store';

const styles = require('./WorkingHours.scss');

/**
 * Working hours component renders start time and end time
 */
export default observer(function WorkingHours() {
    const epoch = utcDate(0);
    const selectedStartTime = addMinutes(epoch, getWorkingHoursStartTimeInMinutes());
    const selectedEndTime = addMinutes(epoch, getWorkingHoursEndTimeInMinutes());

    const timeIncrement = getTimeIncrement();

    const userTimeZoneId = getUserTimeZone();
    const existingWorkingHoursTimeZoneId = getCalendarConfigWorkingHoursTimeZoneId();
    const proposedWorkingHoursTimeZoneId = store().workingHoursTimeZoneId;

    const showWorkingHoursTimeZone = userTimeZoneId != existingWorkingHoursTimeZoneId;
    const workingHoursTimeZoneChecked = userTimeZoneId == proposedWorkingHoursTimeZoneId;

    return (
        <div className={styles.workingHoursWrapper}>
            <Label>{strings.options_workingHoursLabel}</Label>
            <div className={styles.timeWrapper}>
                <div className={styles.time}>
                    <Label>{strings.options_workingHours_workingHoursStartTime}</Label>
                    <TimePicker
                        ariaLabel={strings.options_workingHours_workingHoursStartTime}
                        selectedTime={selectedStartTime}
                        minutesInterval={timeIncrement}
                        onChanged={onStartTimeChange}
                    />
                </div>
                <div className={styles.time}>
                    <Label>{strings.options_workingHours_workingHoursEndTime}</Label>
                    <TimePicker
                        ariaLabel={strings.options_workingHours_workingHoursEndTime}
                        selectedTime={selectedEndTime}
                        minutesInterval={timeIncrement}
                        onChanged={onEndTimeChange}
                        startTime={selectedStartTime}
                    />
                </div>
            </div>
            <div className={styles.timeZoneName}>
                {formatWithTimeZoneName(
                    strings.options_workingHours_timeZoneLabel,
                    existingWorkingHoursTimeZoneId
                )}
            </div>
            {showWorkingHoursTimeZone && (
                <Checkbox
                    checked={workingHoursTimeZoneChecked}
                    label={formatWithTimeZoneName(strings.options_workingHours_changeTimeZoneLabel)}
                    onChange={onUpdateWorkingHoursTimeZoneChange}
                />
            )}
        </div>
    );
});

/**
 * When users selects start time > end time then start/end time
 * values are updated with selected start time and user is shown a dialog message.
 * (The start time that you entered occurs after the end time. The end time must occur after the start time.)
 */
function onStartTimeChange(startTimeDisplayDate: OwaDate) {
    const endTime = getWorkingHoursEndTimeInMinutes();
    const startTime = differenceInMinutes(startTimeDisplayDate, utcDate(0));
    setWorkingHoursStartTimeOption(startTime);
    if (!isValidTimeSelection(startTime, endTime)) {
        setWorkingHoursEndTimeOption(startTime);
    }
}

/**
 * When users selects end time < start time then start/end time
 * values are updated with selected end time and user is shown a dialog message.
 * (The end time that you entered occurs before the start time.The start time must occur before the end time.)
 */
function onEndTimeChange(endTimeDisplayDate: OwaDate) {
    const endTime = differenceInMinutes(endTimeDisplayDate, utcDate(0));
    const startTime = getWorkingHoursStartTimeInMinutes();
    setWorkingHoursEndTimeOption(endTime);
    if (!isValidTimeSelection(startTime, endTime)) {
        setWorkingHoursStartTimeOption(endTime);
    }
}

function onUpdateWorkingHoursTimeZoneChange(
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean
) {
    setWorkingHoursTimeZoneId(checked ? getUserTimeZone() : null);
}
